import { useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  Icon,
  Input,
  Select,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import { formatCount, parsePesos } from '@dayon/money';
import { api, ApiRequestError } from '../../../lib/api.ts';
import { RESTRICTION_KINDS, RESTRICTION_REASONS, type RestrictionKindKey } from '../vocabulary.ts';
import type { RestrictionOutcome } from '../types.ts';

/**
 * Apply one account control to a selection.
 *
 * Shared by the roster's bulk bar and the profile's Restrict button, so the two
 * cannot drift about what a control means or which reasons it accepts. The
 * profile passes a single id; the roster passes up to five hundred. Either way
 * the route takes them in ONE transaction — every player or none of them.
 */
export function RestrictDialog({
  open,
  onClose,
  playerIds,
  subject,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  playerIds: string[];
  /** Whose account it is, when there is exactly one. The profile knows; the
      roster's bulk bar does not, and "12 players" is the honest title there. */
  subject?: string | undefined;
  onDone: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const [kind, setKind] = useState<RestrictionKindKey>('WITHDRAWAL_BLOCK');
  const [reason, setReason] = useState<string>(RESTRICTION_REASONS[0].code);
  const [note, setNote] = useState('');
  const [limit, setLimit] = useState('');
  const [expiry, setExpiry] = useState('');
  const [saving, setSaving] = useState(false);

  const count = playerIds.length;
  const kindLabel = RESTRICTION_KINDS.find((k) => k.kind === kind)?.label ?? kind;

  /* Inline validation, on the field that has the problem. The route checks all
     three again — it is the boundary, this is the courtesy. */
  let limitError: string | undefined;
  if (kind === 'DEPOSIT_LIMIT') {
    if (limit.trim() === '') {
      limitError = 'A deposit limit needs the daily cap it sets.';
    } else {
      try {
        // Centavos, via the one parser. A peso amount never becomes a float.
        if (parsePesos(limit) <= 0n) {
          limitError = 'A cap of zero blocks deposits outright — use a withdrawal block instead.';
        }
      } catch {
        limitError = 'Digits with at most two decimal places, for example 50,000.00';
      }
    }
  }
  const noteError =
    note.length > 0 && note.trim().length < 10
      ? 'At least 10 characters — a code on its own reads as a form number.'
      : undefined;
  const ready = count > 0 && note.trim().length >= 10 && limitError === undefined;

  const submit = async () => {
    if (!ready || saving) return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        playerIds,
        kind,
        reasonCode: reason,
        note: note.trim(),
      };
      if (kind === 'DEPOSIT_LIMIT') payload['limitAmount'] = parsePesos(limit).toString();
      /* An expiry names a DAY, and the control runs to the end of it — an
         expiry at midnight would take the block off the morning before the
         operator meant it to come off. */
      if (expiry !== '') payload['expiresAt'] = new Date(`${expiry}T23:59:59`).toISOString();

      const result = await api.post<RestrictionOutcome>('/api/players/restrictions', payload);

      const moved = result.applied.filter((a) => a.statusMovedToRestricted).length;
      const skippedRefs = result.skipped
        .slice(0, 3)
        .map((s) => s.ref)
        .join(', ');
      toast(
        `${kindLabel} applied to ${formatCount(result.applied.length)} player` +
          `${result.applied.length === 1 ? '' : 's'}` +
          (moved > 0 ? ` · ${formatCount(moved)} now read Restricted` : '') +
          (result.skipped.length > 0
            ? ` · ${formatCount(result.skipped.length)} already under it (${skippedRefs}${
                result.skipped.length > 3 ? '…' : ''
              })`
            : '') +
          ' · written to the audit trail',
      );

      setNote('');
      setLimit('');
      setExpiry('');
      onClose();
      onDone();
    } catch (error: unknown) {
      showError({
        title: `Could not restrict ${formatCount(count)} player${count === 1 ? '' : 's'}`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'Nothing was written. The whole submission is one transaction, so no part of it applied.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={
        subject && count === 1
          ? `Restrict ${subject}?`
          : `Restrict ${formatCount(count)} player${count === 1 ? '' : 's'}?`
      }
      wide
      dismissible={false}
      actions={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!ready || saving}
            /* BOTH notices, consequence first. A sighted operator reads the warn
               banner on their way down the form; somebody on a screen reader
               arrives at this button having heard the fields and would otherwise
               be given only the gap notice, which says what the write touches
               and not that the player is never told. The order is the order it
               is read out in, so the consequence goes first. */
            aria-describedby="players-restrict-consequence players-restrict-gap"
            onClick={() => void submit()}
          >
            {/* Named after WHAT is being decided rather than after how many:
                the profile is looking at one person, so "Apply the control"
                reads as the decision it is, while the roster's bar is looking
                at a selection and "Apply to 1" belongs beside "Apply to 12". */}
            {saving
              ? 'Applying…'
              : subject
                ? 'Apply the control'
                : `Apply to ${formatCount(count)}`}
          </Button>
        </>
      }
    >
      <p className="text-muted" style={{ fontSize: '13px' }}>
        {RESTRICTION_KINDS.find((k) => k.kind === kind)?.effect} Every player in the selection gets
        the same control, the same reason and the same note.
      </p>

      <div className="stack">
        {/*
          WARN RATHER THAN DANGER, AND THE CHOICE IS ABOUT THE WAY BACK.

          The tone contract written beside `.notice-warn` in the design system
          keeps danger for the irreversible and for anything that moves money or
          a reported figure. None of these four controls does either. No ledger
          entry is written: a withdrawal block leaves requests sitting in the
          queue, a deposit cap only refuses the next deposit, and a bonus or
          live-table exclusion takes nothing the player already holds. And every
          one of them can be taken off again — the row is not deleted when it is
          lifted, so the account keeps both the day the control went on and the
          day it came off. Spending danger on a reversible decision would leave
          nothing louder for the verdicts that are final, which is the whole
          point of having two tones.

          WHAT IT SAYS IS THE HALF AN OPERATOR WOULD OTHERWISE LEARN FROM A
          SUPPORT CALL. This route writes the control, moves anyone who was
          Active to Restricted, writes an audit entry — and sends nothing. There
          is no mail on this path at all, and the player's own responsible-gaming
          page reads `player.responsible_gaming_limit`, the limits they set on
          themselves, so an operator's cap is not listed there either. The player
          therefore has no way to learn of this until something they try is
          refused, and that refusal reaches them wherever they happen to be
          rather than on a screen the operator chose. "They were not told" is
          the fact worth putting in front of the press.

          The last sentence used to sit in the paragraph above, and its wording is
          unchanged. It belongs here because the way back belongs beside the
          consequence — and it now also says what lifting COSTS, which the
          paragraph did not: Account controls on the profile lifts one control
          for one player, so a selection of twelve is twelve lifts and there is
          no bulk undo for this.
        */}
        <div className="notice notice-warn" id="players-restrict-consequence">
          <Icon name="warning" />
          <div>
            Nothing is sent to the player: no email, no message, and the responsible-gaming page in
            their account lists only the limits they set themselves. They find out when the thing
            they try is refused, so for most people the first word of it is a call to support. It is
            reversible per player from their profile — one control at a time, under Account
            controls.
          </div>
        </div>

        <Field
          label="Control"
          hint="One control per submission — a mixed submission cannot be lifted as one decision."
        >
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={kind}
              onChange={(e) => setKind(e.target.value as RestrictionKindKey)}
            >
              {RESTRICTION_KINDS.map((k) => (
                <option key={k.kind} value={k.kind}>
                  {k.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        {kind === 'DEPOSIT_LIMIT' && (
          <Field
            label="Daily cap (PHP)"
            error={limitError}
            hint="Kept in centavos as the control's amount. No period is kept with it, so the profile prints the amount and never invents “/ day”."
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                aria-invalid={invalid}
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                placeholder="50,000.00"
                inputMode="decimal"
              />
            )}
          </Field>
        )}

        {/* The code is stored on the control and grouped by the reports. No player screen,
            refusal or email reads it: the refusal a player meets is the same whatever the
            reason, and this dialog sends nothing (staging, 15 Oct, part 2). */}
        <Field
          label="Reason"
          hint="Kept with the control, and what a compliance report counts controls by. The player is never told it."
        >
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            >
              {RESTRICTION_REASONS.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field
          label="Note"
          error={noteError}
          hint="At least 10 characters. It travels with the audit entry — this is what the next operator reads before deciding whether to lift it."
        >
          {({ id, describedBy, invalid }) => (
            <textarea
              id={id}
              className="input"
              rows={3}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Payout pattern flagged by the ratio hold; holding until the review closes."
            />
          )}
        </Field>

        <Field
          label="Expires"
          hint="Optional. Left empty the control is open-ended and stays until someone lifts it. A date runs to the end of that day."
        >
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="date"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
            />
          )}
        </Field>

        <div className="notice notice-quiet" id="players-restrict-gap">
          <Icon name="info" />
          <div>
            <strong>What this writes.</strong> One{' '}
            restriction row per player, an{' '}
            <em>Active</em> → <em>Restricted</em> move on{' '}
            status-history for those who were Active,
            and one audit entry each — in a single
            transaction. A player already under this control is skipped by name rather than given a
            second row, because two rows of one kind make “lift it” ambiguous.{' '}
            <strong>Self-exclusion is not offered.</strong> The platform only refuses a lift on a
            self-exclusion the PLAYER set, so a staff-applied self-exclusion would be a staff-liftable one — a full
            account lock that is not one. And{' '}
            {/* No mail template covers an operator's control: the CHECK on
                mail_message.template closes the list, so this path records no outbox message. */}
            <strong>&ldquo;every one is notified&rdquo; still has no home</strong>: the platform has
            no email for a control an operator applies, so none is sent and none is claimed.
          </div>
        </div>
      </div>
    </Dialog>
  );
}
